import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  createEbBankingConsentByCode,
  createEbBankingUrlByPsuType,
  getEbBanksByCountry,
  removeAllActiveEbBankingConsent,
} from 'client/api/banking'
import { Bank, BankAccount, BankingConsentRequest, BankingState, BankingUrlRequest } from 'client/Interfaces/Banking'

import { BankingProviders, BankingSupportedCountries, ProvidersAndCountriesMap } from './BankingModals/ConnectBankModal'

const bankingInitialState: BankingState = {
  banks: [],
  selectedBankAccounts: [],
  bankingUrl: null,
  selectedBankId: null,
  bankingConsentStatus: null,
  status: 'idle',
  error: null,
}

export const fetchBanksByCountry = createAsyncThunk('banks/fetchBanks', async (country: string): Promise<Bank[]> => {
  const allBanks = []
  if (ProvidersAndCountriesMap[BankingSupportedCountries[country]].includes(BankingProviders.eb)) {
    const ebResponse = await getEbBanksByCountry(country)
    ebResponse.banks.forEach((bank) => {
      // put bankName as bankId temporarily as eb banks response does not contain bankId
      const updatedBank = { ...bank, ...{ id: bank.name } }
      allBanks.push(updatedBank)
    })
  }
  return allBanks
})

export const createBankingUrl = createAsyncThunk('bankingUrl/createBankingUrl', async (request: BankingUrlRequest) => {
  if (request.psuType) {
    return await createEbBankingUrlByPsuType(request)
  }
})

export const createBankingConsent = createAsyncThunk(
  'bankingConsent/createBankingConsent',
  async (request: BankingConsentRequest) => {
    if (request.selectedBankingProvider === BankingProviders.eb) {
      const EbResponse = await createEbBankingConsentByCode(request.consentCodeBody)
      return EbResponse.status
    }
  },
)

export const removeBankingConsent = createAsyncThunk(
  'bankingConsent/removeBankingConsent',
  async (selectedBankingProvider: string) => {
    if (selectedBankingProvider === BankingProviders.eb) {
      const res = await removeAllActiveEbBankingConsent()
      return res.status
    }
  },
)

const banking = createSlice({
  name: 'banking',
  initialState: bankingInitialState,
  reducers: {
    setSelectedBankAccount(state, action: PayloadAction<BankAccount[]>) {
      state.selectedBankAccounts = action.payload
    },
  },
  extraReducers: (builder) => {
    builder

      // pending
      .addCase(fetchBanksByCountry.pending, (state, action) => {
        state.status = 'fetchingBanksByCountry'
      })
      .addCase(createBankingUrl.pending, (state, action) => {
        state.status = 'creatingBankingUrl'
      })
      .addCase(createBankingConsent.pending, (state, action) => {
        state.status = 'creatingBankingConsent'
      })
      .addCase(removeBankingConsent.pending, (state, action) => {
        state.status = 'removingBankingConsent'
      })

      // fulfilled
      .addCase(fetchBanksByCountry.fulfilled, (state, action) => {
        state.status = 'succeed'
        state.banks = action.payload
      })
      .addCase(createBankingUrl.fulfilled, (state, action) => {
        state.status = 'succeed'
        state.bankingUrl = action.payload.bankingUrl
        state.selectedBankId = action.payload.bankId
      })
      .addCase(createBankingConsent.fulfilled, (state, action) => {
        state.status = 'succeed'
        state.bankingConsentStatus = action.payload
      })
      .addCase(removeBankingConsent.fulfilled, (state, action) => {
        state.status = 'idle'
        state.bankingConsentStatus = action.payload
      })

      // rejected
      .addCase(fetchBanksByCountry.rejected, (state, action) => {
        state.error = action.error
        state.status = 'failed'
      })
      .addCase(createBankingUrl.rejected, (state, action) => {
        state.error = action.error
        state.status = 'failed'
      })
      .addCase(createBankingConsent.rejected, (state, action) => {
        state.error = action.error
        state.status = 'failed'
      })
      .addCase(removeBankingConsent.rejected, (state, action) => {
        state.error = action.error
        state.status = 'failed'
      })
  },
})

export const { setSelectedBankAccount } = banking.actions
export default banking.reducer
